/**
 * 分区标题编辑窗口。app_home_sections 按语言拆行，本窗口只编辑当前语言 Tab 的单 key 标题。
 */

import { useState } from 'react';
import type { HomeBlock } from '~/data/queries';
import { langName } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { Dialog } from '~/ui/Dialog';
import { Select } from '~/ui/Select';
import { toast } from '~/ui/Toast';
import { OpsSrcChip, deriveOpsSrc } from './OpsSrcChip';
import { isOpsGlobal } from './ops-scope';
import { type OpsMutations, errorToast, useCanUpsertOps } from './use-ops-mutations';

export function TitleLangsDialog({
  sectionName,
  sectionKey,
  block,
  appId,
  lang,
  mutations,
  onClose,
}: {
  sectionName: string;
  sectionKey: string;
  block: HomeBlock;
  appId: string;
  lang: string;
  mutations: OpsMutations;
  onClose: () => void;
}) {
  const canWrite = useCanUpsertOps();
  const [layout, setLayout] = useState(block.layout);
  const global = isOpsGlobal(appId);
  const langs = [lang];

  const [draft, setDraft] = useState<Record<string, string>>(() =>
    langs.reduce<Record<string, string>>((acc, l) => {
      acc[l] = block.titleI18n?.[l] ?? '';
      return acc;
    }, {}),
  );
  return (
    <Dialog
      width={760}
      icon="IconLanguages"
      title={`分区标题 · ${sectionName}`}
      subtitle={
        global
          ? `全部（该语言全局）· 只维护当前语言 ${lang.toUpperCase()}`
          : `覆盖 · 只维护当前语言 ${lang.toUpperCase()}`
      }
      onClose={onClose}
      footer={
        <>
          <div style={{ flex: 1, fontSize: 11, color: 'var(--fg-muted)' }}>
            当前行只有当前语言一个 key，不要求英语
          </div>
          <button type="button" className="btn" onClick={onClose}>
            取消
          </button>
          <button
            type="button"
            className="btn btn-accent"
            disabled={!canWrite || mutations.updateSection.isPending || !draft[lang]?.trim()}
            onClick={() => {
              mutations.updateSection.mutate(
                {
                  id: block.id,
                  body: {
                    title_i18n: { [lang]: draft[lang] ?? '' },
                    ...(layout !== block.layout ? { layout } : {}),
                  },
                },
                {
                  onSuccess: () => {
                    toast(`已保存「${sectionName}」标题 · ${langs.length} 门语言`, {
                      tone: 'pos',
                      icon: 'IconCheck',
                    });
                    onClose();
                  },
                  onError: (err) => errorToast(err, '保存标题失败'),
                },
              );
            }}
          >
            <Icons.IconCheck size={11} />
            保存
          </button>
        </>
      }
    >
      <div style={{ padding: 18 }}>
        <div
          style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginBottom: 12, lineHeight: 1.6 }}
        >
          {sectionKey} · 维护标题与布局；书单在「首页每周精选书籍」Tab 维护。
        </div>
        <div className="hstack" style={{ gap: 8, marginBottom: 12 }}>
          <span>展示布局</span>
          <Select
            value={layout}
            disabled={!canWrite || mutations.updateSection.isPending}
            onChange={(event) => setLayout(event.target.value as HomeBlock['layout'])}
          >
            <option value="HORIZONTAL">横排 · 最多 10 本</option>
            <option value="VERTICAL">竖排 · 最多 5 本</option>
          </Select>
        </div>
        <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginBottom: 12 }}>
          改为竖排前请先将每周精选的绑定数减至 5 本以内；超额时保存会被拒绝，书单不会截断。
        </div>
        <div className="vstack" style={{ gap: 8 }}>
          {langs.map((l) => (
            <div key={l} className="hstack" style={{ gap: 8 }}>
              <span
                className="chip mono"
                style={{
                  fontSize: 10,
                  minWidth: 92,
                  justifyContent: 'center',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                {l.toUpperCase()} {langName(l)}
              </span>
              <input
                className="input"
                disabled={!canWrite || mutations.updateSection.isPending}
                value={draft[l] ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, [l]: e.target.value }))}
                placeholder={`${langName(l)}标题`}
                style={{ flex: '1 1 0', minWidth: 0 }}
              />
              <span style={{ flexShrink: 0 }}>
                <OpsSrcChip src={deriveOpsSrc(l, draft[l] ?? '')} />
              </span>
            </div>
          ))}
        </div>
      </div>
    </Dialog>
  );
}
